import {Component, DOCUMENT, inject, OnInit} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {MemoryService} from '../../../core/services/memory.service';
import {LocationService} from '../../../core/services/location.service';
import {ErrorService} from '../../../core/services/error.service';
import {TranslocoService} from '@jsverse/transloco';
import {WindowReferenceService} from '../../../core/services/window.service';
import {AuthenticationService} from '../../authentication/authentication.service';
import {ExchangeTokenDto} from '../../authentication/dtos/exchange-token.dto';
import {RefreshDto} from '../../authentication/dtos/refresh.dto';
import {filter, finalize, startWith} from 'rxjs';
import {LoginResponseDto} from '../../authentication/dtos/login-response.dto';
import {LocationQueryDto} from '../dtos/location-query.dto';
import {LocationDetailDto} from '../dtos/location-detail.dto';
import {HttpErrorResponse} from '@angular/common/http';

@Component({
  selector: 'app-startup',
  imports: [
    RouterOutlet
  ],
  templateUrl: './startup.component.html',
  styleUrl: './startup.component.scss'
})
export class StartupComponent implements OnInit {
  route = inject(ActivatedRoute);
  router = inject(Router);
  memoryService = inject(MemoryService);
  locationService = inject(LocationService);
  authenticationService = inject(AuthenticationService);
  errorService = inject(ErrorService);
  translocoService = inject(TranslocoService);
  windowSerivce = inject(WindowReferenceService);
  document: Document = inject(DOCUMENT);

  isProfileRoute?: boolean;

  private patientDataIsInitializing = false;
  private bookingDataIsInitializing = false;

  ngOnInit() {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      startWith(null)
    ).subscribe(() => {
      this.delegateInitialization()
    })
  }

  delegateInitialization() {
    if (!this.patientDataIsInitializing && !this.memoryService.patientDataInitialized) {
      this.patientDataIsInitializing = true;
      this.checkExchangeToken()
    }

    if (!this.bookingDataIsInitializing && !this.memoryService.bookingDataInitialized) {
      this.bookingDataIsInitializing = true;
      this.initializeBookingData();
    }
  }

  checkExchangeToken() {
    const params = this.route.snapshot.queryParams;
    const exchangeToken: string | undefined = params['token'];

    if (exchangeToken) {
      const exchangeTokenDto: ExchangeTokenDto = {
        oneTimeToken: exchangeToken
      }

      this.authenticationService.redeemExchangeToken(exchangeTokenDto)
        .pipe(
          finalize(() => {
              this.initializePatientData();
            }
          ))
        .subscribe({
          next: (refreshDto: RefreshDto) => {
            this.memoryService.refreshToken = refreshDto.refreshToken;
          },
        })
    } else {
      this.initializePatientData();
    }
  }

  initializePatientData() {
    if (this.memoryService.refreshToken) {
      const refreshDto: RefreshDto = {refreshToken: this.memoryService.refreshToken};

      this.authenticationService.refresh(refreshDto)
        .pipe(
          finalize(() => {
              this.finishPatientDataInitialization();
            }
          ))
        .subscribe({
          next: (loginResponseDto: LoginResponseDto) => {
            this.memoryService.refreshToken = loginResponseDto.refreshToken;
            this.memoryService.patientId = loginResponseDto.patientId;
            this.memoryService.accessToken = loginResponseDto.accessToken;
            this.memoryService.isNewUser = loginResponseDto.isNew;
            this.memoryService.patientEmail = loginResponseDto.email;

            this.memoryService.patientDataInitialized = true
          }
        })
    } else {
      this.finishPatientDataInitialization();
    }
  }

  finishPatientDataInitialization() {
    this.patientDataIsInitializing = false;
    if (this.router.url.startsWith("/profile") || this.router.url.startsWith("/login")) {
      this.isProfileRoute = true;
    }
  }

  initializeBookingData() {
    const params = this.route.snapshot.queryParams;
    let customerId = this.parseIntOrUndefined(params['customerId']);
    let locationId = this.parseIntOrUndefined(params['locationId']);
    const categoryId = this.parseIntOrUndefined(params['categoryId']);
    const employeeId = this.parseIntOrUndefined(params['employeeId']);
    const categoryGroup: string | undefined = params['categoryGroup'];
    const parentOrigin = params['gtmorigin'];

    if (customerId && locationId) {
      if (parentOrigin) {
        this.memoryService.gtmOrigin = parentOrigin;
        this.windowSerivce.init(customerId);
      }

      this.memoryService.customerId = customerId;
      this.memoryService.locationId = locationId;
      this.memoryService.categoryId = categoryId;
      this.memoryService.employeeId = employeeId;
      this.memoryService.categoryGroup = categoryGroup;

      this.loadLocation();
      return;
    }

    customerId = this.memoryService.customerId;
    locationId = this.memoryService.locationId;

    if (customerId && locationId) {
      this.windowSerivce.init(customerId);
      this.loadLocation();
      return;
    }

    this.bookingDataIsInitializing = false;
  }

  loadLocation() {
    const locationId = this.memoryService.locationId;
    if (!locationId) {
      this.bookingDataIsInitializing = false;
      return;
    }

    const locationQueryDto: LocationQueryDto = {customerId: this.memoryService.customerId!, locationIds: [locationId]};
    this.locationService.queryLocationDetails(locationQueryDto).subscribe({
      next: (locationDetailDtos: LocationDetailDto[]) => {
        const locationDetailDto = locationDetailDtos.find(l => l.locationId == locationId);
        if (!locationDetailDto) {
          this.errorService.translocoService.selectTranslate('error.title.location').subscribe(title => {
            this.errorService.handleError(new HttpErrorResponse({status: 404}), this.translocoService.translate(title));
          });
          this.bookingDataIsInitializing = false;
          return;
        }

        this.setProperty("--custom-primary-color", locationDetailDto.appearanceSettings.primaryColor);
        this.setProperty("--custom-secondary-color", locationDetailDto.appearanceSettings.secondaryColor);
        this.setProperty("--custom-tertiary-color", locationDetailDto.appearanceSettings.tertiaryColor);

        this.memoryService.locationDetailDto = locationDetailDto;
        this.memoryService.bookingDataInitialized = true;
        this.bookingDataIsInitializing = false;
      },
      error: (error: HttpErrorResponse) => {
        this.errorService.translocoService.selectTranslate('error.title.location').subscribe(title => {
          this.errorService.handleError(error, this.translocoService.translate(title));
        });
        this.bookingDataIsInitializing = false;
      }
    })
  }

  private setProperty(propertyName: string, value?: string) {
    if (!value) {
      return;
    }

    this.document.documentElement.style.setProperty(propertyName, value)
  }

  private parseIntOrUndefined(value: string | undefined): number | undefined {
    if (!value) {
      return undefined;
    }

    const result = parseInt(value);
    return isNaN(result) ? undefined : result;
  }
}
